/**
 * YayCustomerSearchModal — Modal for two purposes:
 *
 * 1. **Link mode** (mode='link'): Search customers and double-click to link
 *    an unknown caller's phone number to a customer account.
 *
 * 2. **Manage mode** (mode='manage'): Browse and delete all user-added phone
 *    number mappings (CustomerPhoneMappings).
 *
 * Both modes share the customer grid; link mode adds double-click selection,
 * manage mode shows saved numbers with per-row delete.
 *
 * Props:
 *   - mode: 'link' | 'manage'
 *   - phone: string — the phone number to link (link mode)
 *   - callId: string — the active call ID (link mode)
 *   - onLink(callId, accountNumber, customerName, phone): async callback
 *   - onClose(): close the modal
 *   - isDark: boolean
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import axios from 'axios';
import oapiAxios from '../axiosInstance';
import { fetchOrderwiseToken } from '../orderwiseApi';
import useGridLayout from '../utils/useGridLayout';
import GridSaveLayoutButton from './GridSaveLayoutButton';

export default function YayCustomerSearchModal({
  mode = 'link',
  phone = '',
  callId = '',
  onLink,
  onClose,
  isDark = false,
}) {
  // ─── Tab state (link mode has customers tab only; manage mode has both) ──
  const [tab, setTab] = useState(mode === 'manage' ? 'mappings' : 'customers');

  // ─── Customer grid state ──────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState(null); // tracks account just linked for success message
  const [selectedRow, setSelectedRow] = useState(null); // currently selected customer row
  const gridRef = useRef(null);
  const customerSearchLayout = useGridLayout('yay-customer-search-grid');

  // ─── Mappings state ───────────────────────────────────────
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState('');
  const [mappingFilter, setMappingFilter] = useState('');
  const mappingsGridRef = useRef(null);
  const mappingsLayout = useGridLayout('yay-phone-mappings-grid');
  const mappingsFilterRef = useRef('');

  // ─── Load customers ───────────────────────────────────────

  useEffect(() => {
    if (tab !== 'customers' && mode !== 'link') return; // defer until needed
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = await fetchOrderwiseToken();
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const pimJwt = localStorage.getItem('token');
        if (pimJwt) headers['X-PIM-JWT'] = pimJwt;

        const { data } = await oapiAxios.get('/customers', { headers });
        if (cancelled) return;
        const rows = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setCustomers(rows);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'Failed to load customers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, mode]);

  // ─── Load mappings ────────────────────────────────────────

  const loadMappings = useCallback(async () => {
    setMappingsLoading(true);
    setMappingsError('');
    try {
      const { data } = await axios.get('/api/yay/phone-mappings');
      setMappings(Array.isArray(data?.mappings) ? data.mappings : []);
    } catch (err) {
      setMappingsError(err?.response?.data?.error || err.message || 'Failed to load saved numbers');
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'mappings') loadMappings();
  }, [tab, loadMappings]);

  // ─── Delete mapping ───────────────────────────────────────

  const handleDeleteMapping = useCallback(async (id) => {
    if (!window.confirm('Remove this saved phone number?')) return;
    try {
      await axios.delete(`/api/yay/phone-mappings/${id}`);
      setMappings((prev) => prev.filter((m) => m.Id !== id));
    } catch (err) {
      console.error('Failed to delete mapping:', err);
      setMappingsError('Failed to delete — please try again.');
    }
  }, []);

  // ─── Customer column definitions ──────────────────────────

  const columnDefs = useMemo(() => [
    { headerName: 'Account', field: 'accountNumber', width: 120, flex: 0 },
    { headerName: 'Name', field: 'statementName', flex: 2 },
    { headerName: 'Address 1', field: 'statementAddress1', width: 160, flex: 0 },
    { headerName: 'Address 2', field: 'statementAddress2', width: 140, flex: 0 },
    { headerName: 'Address 3', field: 'statementAddress3', width: 140, flex: 0 },
    { headerName: 'Town', field: 'statementTown', width: 140, flex: 0 },
    { headerName: 'Postcode', field: 'statementPostcode', width: 120, flex: 0 },
    { headerName: 'Telephone', field: 'statementTelephone', width: 150, flex: 0 },
    { headerName: 'Email', field: 'statementEmail', flex: 1 },
    { headerName: 'Country', field: 'statementCountry', width: 100, flex: 0 },
  ], []);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
    minWidth: 60,
  }), []);

  // ─── Mappings column definitions ──────────────────────────

  const mappingColumnDefs = useMemo(() => [
    { headerName: 'Account', field: 'AccountNumber', width: 120 },
    { headerName: 'Customer', field: 'CustomerName', flex: 1 },
    { headerName: 'Phone (Saved)', field: 'PhoneRaw', width: 160 },
    { headerName: 'Normalised', field: 'PhoneNormalized', width: 150 },
    { headerName: 'Label', field: 'Label', width: 100 },
    { headerName: 'Added By', field: 'CreatedByUsername', width: 130 },
    {
      headerName: 'Added',
      field: 'CreatedAt',
      width: 150,
      valueFormatter: (p) => {
        if (!p.value) return '';
        try { return new Date(p.value).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }); }
        catch { return p.value; }
      },
    },
    {
      headerName: '',
      field: 'Id',
      width: 70,
      sortable: false,
      filter: false,
      cellRenderer: (p) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleDeleteMapping(p.value); }}
          title="Remove this saved number"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#ef4444',
            fontSize: 15,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >
          ✕
        </button>
      ),
    },
  ], [handleDeleteMapping]);

  const mappingDefaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
    minWidth: 50,
  }), []);

  // ─── Quick filter (external, for customer grid) ───────────

  const quickFilterRef = useRef('');

  useEffect(() => {
    const t = setTimeout(() => {
      quickFilterRef.current = quickFilter;
      try { gridRef.current?.api?.onFilterChanged(); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [quickFilter]);

  const isExternalFilterPresent = useCallback(() => {
    return (quickFilterRef.current || '').trim().length > 0;
  }, []);

  const doesExternalFilterPass = useCallback((node) => {
    const q = (quickFilterRef.current || '').trim().toLowerCase();
    if (!q) return true;
    const tokens = q.split(/\s+/);
    const d = node.data || {};
    const hay = [
      d.accountNumber,
      d.statementName,
      d.statementAddress1,
      d.statementAddress2,
      d.statementAddress3,
      d.statementTown,
      d.statementPostcode,
      d.statementTelephone,
      d.statementEmail,
      d.statementCountry,
    ].filter(Boolean).join(' ').toLowerCase();
    return tokens.every((t) => hay.includes(t));
  }, []);

  // ─── Mappings filter (external) ───────────────────────────

  useEffect(() => {
    const t = setTimeout(() => {
      mappingsFilterRef.current = mappingFilter;
      try { mappingsGridRef.current?.api?.onFilterChanged(); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [mappingFilter]);

  const isMappingsFilterPresent = useCallback(() => {
    return (mappingsFilterRef.current || '').trim().length > 0;
  }, []);

  const doesMappingsFilterPass = useCallback((node) => {
    const q = (mappingsFilterRef.current || '').trim().toLowerCase();
    if (!q) return true;
    const tokens = q.split(/\s+/);
    const d = node.data || {};
    const hay = [
      d.AccountNumber, d.CustomerName, d.PhoneRaw, d.PhoneNormalized,
      d.Label, d.CreatedByUsername,
    ].filter(Boolean).join(' ').toLowerCase();
    return tokens.every((t) => hay.includes(t));
  }, []);

  // ─── Double-click → link customer (link mode) ────────────

  const handleRowDoubleClicked = useCallback(async (params) => {
    if (mode !== 'link') return;
    const row = params.data;
    if (!row || linking) return;
    const acct = row.accountNumber;
    const name = row.statementName || '';
    if (!acct) return;

    setLinking(true);
    try {
      if (onLink) {
        await onLink(callId, acct, name, phone);
      }
      // Show the saved numbers tab so the user can see the newly added mapping
      setLinkedAccount(acct);
      setLinking(false);
      setTab('mappings');
      loadMappings();
    } catch (err) {
      console.error('Failed to link customer:', err);
      setError('Failed to save — please try again.');
      setLinking(false);
    }
  }, [mode, callId, phone, onLink, onClose, linking, loadMappings]);

  // Single-click → select row (for the Link button)
  const handleRowClicked = useCallback((params) => {
    if (mode !== 'link') return;
    const row = params.data;
    if (!row) return;
    setSelectedRow(row);
  }, [mode]);

  // Link button handler (uses selectedRow)
  const handleLinkSelected = useCallback(async () => {
    if (!selectedRow || linking) return;
    const acct = selectedRow.accountNumber;
    const name = selectedRow.statementName || '';
    if (!acct) return;

    setLinking(true);
    try {
      if (onLink) {
        await onLink(callId, acct, name, phone);
      }
      // Show the saved numbers tab so the user can see the newly added mapping
      setLinkedAccount(acct);
      setLinking(false);
      setSelectedRow(null);
      setTab('mappings');
      loadMappings();
    } catch (err) {
      console.error('Failed to link customer:', err);
      setError('Failed to save — please try again.');
      setLinking(false);
    }
  }, [selectedRow, mode, callId, phone, onLink, onClose, linking, loadMappings]);

  // ─── Styles ───────────────────────────────────────────────

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    padding: 16,
  };

  const panelStyle = {
    background: isDark ? '#1e293b' : '#ffffff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 1400,
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    color: isDark ? '#e2e8f0' : '#1e293b',
  };

  const headerStyle = {
    padding: '16px 20px',
    borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const searchStyle = {
    padding: '12px 20px 0',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#f8fafc',
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontSize: 14,
    outline: 'none',
  };

  const gridContainerStyle = {
    flex: 1,
    padding: '12px 20px',
    minHeight: 0,
  };

  const footerStyle = {
    padding: '12px 20px',
    borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 13,
  };

  const tabBtnStyle = (active) => ({
    padding: '6px 16px',
    borderRadius: '6px 6px 0 0',
    border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    borderBottom: active ? 'none' : `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    background: active ? (isDark ? '#1e293b' : '#ffffff') : (isDark ? '#0f172a' : '#f1f5f9'),
    color: isDark ? '#e2e8f0' : '#1e293b',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    marginRight: 4,
    position: 'relative',
    bottom: -1,
  });

  const isLinkMode = mode === 'link';
  const showTabs = true; // Always show tabs — customers + saved numbers

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              {isLinkMode ? 'Link to Customer' : 'Phone Number Management'}
            </h3>
            <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
              {isLinkMode ? (
                <>Select a customer and click <strong>Link</strong>, or double-click a row, to link <strong>{phone}</strong></>
              ) : (
                'View and manage saved phone-to-customer mappings'
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 22,
              color: isDark ? '#94a3b8' : '#64748b',
              padding: '0 4px',
              lineHeight: 1,
            }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* Tabs */}
        {showTabs && (
          <div style={{ padding: '0 20px', borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, display: 'flex' }}>
            <button
              style={tabBtnStyle(tab === 'customers')}
              onClick={() => { setTab('customers'); setLinkedAccount(null); }}
            >
              {isLinkMode ? 'Select Customer' : 'Customer List'}
            </button>
            <button
              style={tabBtnStyle(tab === 'mappings')}
              onClick={() => setTab('mappings')}
            >
              Saved Numbers
            </button>
          </div>
        )}

        {/* ─── Customers tab ──────────────────────────────── */}
        {tab === 'customers' && (
          <>
            <div style={searchStyle}>
              <input
                type="text"
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                placeholder="Search by account, name, town, postcode, telephone, email..."
                style={inputStyle}
                autoFocus
              />
              <GridSaveLayoutButton onSave={customerSearchLayout.saveLayout} style={{ fontSize: 12, whiteSpace: 'nowrap' }} />
              <button type="button" onClick={customerSearchLayout.resetToDefault} style={{ fontSize: 12, whiteSpace: 'nowrap' }} title="Reset column layout">↻ Reset Columns</button>
              {isLinkMode && (
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6, textAlign: 'center' }}>
                  Click a row then press Link, or double-click to link instantly
                </div>
              )}
            </div>

            <div style={gridContainerStyle}>
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 15, opacity: 0.6 }}>
                  Loading customers...
                </div>
              ) : error && customers.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 15, color: '#ef4444' }}>
                  {error}
                </div>
              ) : (
                <div
                  className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
                  style={{ height: '100%', width: '100%' }}
                >
                  <AgGridReact
                    theme="legacy"
                    ref={gridRef}
                    rowData={customers}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(p) => String(p.data?.accountNumber || p.data?.id)}
                    isExternalFilterPresent={isExternalFilterPresent}
                    doesExternalFilterPass={doesExternalFilterPass}
                    rowSelection="single"
                    onRowDoubleClicked={handleRowDoubleClicked}
                    onRowClicked={handleRowClicked}
                    animateRows={false}
                    pagination={true}
                    paginationPageSize={50}
                    suppressCellFocus={true}
                    rowStyle={{ cursor: isLinkMode ? 'pointer' : 'default' }}
                    onGridReady={customerSearchLayout.onGridReady}
                    onColumnMoved={customerSearchLayout.onColumnChanged}
                    onColumnResized={customerSearchLayout.onColumnResized}
                    onColumnVisible={customerSearchLayout.onColumnChanged}
                    onColumnPinned={customerSearchLayout.onColumnChanged}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Mappings tab ───────────────────────────────── */}
        {tab === 'mappings' && (
          <>
            {linkedAccount && (
              <div style={{
                margin: '10px 20px 0',
                padding: '8px 14px',
                borderRadius: 6,
                background: isDark ? '#064e3b' : '#dcfce7',
                color: isDark ? '#6ee7b7' : '#166534',
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span>✓ Linked <strong>{phone}</strong> to <strong>{linkedAccount}</strong></span>
                <button
                  onClick={() => setLinkedAccount(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, padding: '0 4px' }}
                >×</button>
              </div>
            )}
            <div style={searchStyle}>
              <input
                type="text"
                value={mappingFilter}
                onChange={(e) => setMappingFilter(e.target.value)}
                placeholder="Filter by account, customer, phone, label..."
                style={inputStyle}
                autoFocus
              />
              <GridSaveLayoutButton onSave={mappingsLayout.saveLayout} style={{ fontSize: 12, whiteSpace: 'nowrap' }} />
              <button type="button" onClick={mappingsLayout.resetToDefault} style={{ fontSize: 12, whiteSpace: 'nowrap' }} title="Reset column layout">↻ Reset Columns</button>
            </div>

            <div style={gridContainerStyle}>
              {mappingsLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 15, opacity: 0.6 }}>
                  Loading saved numbers...
                </div>
              ) : mappingsError && mappings.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 15, color: '#ef4444' }}>
                  {mappingsError}
                </div>
              ) : mappings.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 15, opacity: 0.5 }}>
                  No saved phone numbers yet. Numbers are added when linking unknown callers to customers.
                </div>
              ) : (
                <div
                  className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
                  style={{ height: '100%', width: '100%' }}
                >
                  <AgGridReact
                    theme="legacy"
                    ref={mappingsGridRef}
                    rowData={mappings}
                    columnDefs={mappingColumnDefs}
                    defaultColDef={mappingDefaultColDef}
                    getRowId={(p) => String(p.data?.Id)}
                    isExternalFilterPresent={isMappingsFilterPresent}
                    doesExternalFilterPass={doesMappingsFilterPass}
                    animateRows={false}
                    pagination={true}
                    paginationPageSize={50}
                    suppressCellFocus={true}
                    onGridReady={mappingsLayout.onGridReady}
                    onColumnMoved={mappingsLayout.onColumnChanged}
                    onColumnResized={mappingsLayout.onColumnResized}
                    onColumnVisible={mappingsLayout.onColumnChanged}
                    onColumnPinned={mappingsLayout.onColumnChanged}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div style={footerStyle}>
          <span style={{ opacity: 0.7 }}>
            {tab === 'customers'
              ? (customers.length > 0 ? `${customers.length} customers` : '')
              : (mappings.length > 0 ? `${mappings.length} saved number${mappings.length !== 1 ? 's' : ''}` : '')
            }
            {error && tab === 'customers' && customers.length > 0 && (
              <span style={{ color: '#ef4444', marginLeft: 12 }}>{error}</span>
            )}
            {mappingsError && tab === 'mappings' && mappings.length > 0 && (
              <span style={{ color: '#ef4444', marginLeft: 12 }}>{mappingsError}</span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isLinkMode && tab === 'customers' && (
              <button
                onClick={handleLinkSelected}
                disabled={!selectedRow || linking}
                style={{
                  padding: '6px 18px',
                  borderRadius: 6,
                  border: 'none',
                  background: selectedRow && !linking ? '#2563eb' : (isDark ? '#334155' : '#e2e8f0'),
                  color: selectedRow && !linking ? '#fff' : (isDark ? '#64748b' : '#94a3b8'),
                  cursor: selectedRow && !linking ? 'pointer' : 'default',
                  fontSize: 13,
                  fontWeight: 600,
                  transition: 'background 0.15s',
                }}
                title={selectedRow ? `Link ${phone} to ${selectedRow.accountNumber}` : 'Select a customer first'}
              >
                {linking ? 'Linking…' : selectedRow ? `🔗 Link to ${selectedRow.accountNumber}` : '🔗 Link'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '6px 16px',
                borderRadius: 6,
                border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                background: isDark ? '#334155' : '#f1f5f9',
                color: isDark ? '#e2e8f0' : '#1e293b',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
